import { useQuery } from "@tanstack/react-query";
import { get_providers } from "../api/api-methods";
import { useSelector, useDispatch } from "react-redux";
import { setProviders } from "../redux-store/features/providerSlice";
import { useMemo } from "react";
import { dedupeProvidersById } from "../utils/discoveryGridSort";

export const useProviderData = (
  currentPage,
  selectedIds,
  rfiIds,
  providerTypes,
  indications,
  companySizeFiltersId,
  selectedRevenueRange,
  revenueRangeString,
  reportAvailable,
  dataAvailability,
  countries, states, cities,
  searchText,
  sortOptions,
  /** Bumps on each header sort click so asc→desc→default does not reuse a stale query entry. */
  sortFetchNonce = 0
) => {
  const dispatch = useDispatch();
  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["discovery"]
    ) || "";

  const includeReportAvailable =
    reportAvailable?.is_rfi_available === true ||
    reportAvailable?.is_audit_report_available === true;

  const includeRangeRevenue =
    selectedRevenueRange?.min != null && selectedRevenueRange?.max != null;

  const sortBy = sortOptions?.orderBy ?? null;
  const sortDirection = sortOptions?.sortDirection ?? null;

  const payload = useMemo(
    () => ({
      page_size: 21,
      current_page: currentPage,
      ...(selectedIds?.length && { provider_id_list: selectedIds }),
      ...(rfiIds?.length && { rfi_template_id_list: rfiIds }),
      ...(providerTypes?.length && { provider_type_id_list: providerTypes }),
      ...(indications?.length && { indication_id_list: indications }),
      ...(revenueRangeString?.length && {
        revenue_range_string: revenueRangeString,
      }),
      ...(companySizeFiltersId?.length && {
        employee_count_range_list: companySizeFiltersId,
      }),
      ...(includeRangeRevenue && {
        revenue_range: selectedRevenueRange,
      }),
      ...(includeReportAvailable && { report_availability: reportAvailable }),
      ...(dataAvailability?.length && { data_availability: dataAvailability }),
      ...(countries?.length && { country_list: countries.map(c => c === 'US' ? 'USA' : c) }),
      ...(states?.length && { state_list: states }),
      ...(cities?.length && { city_list: cities }),
      search_text: (searchQuery != null && searchQuery !== "") ? encodeURIComponent(searchQuery) : "",
      discovery_status: true,
      ...(sortBy
        ? { sort_by: sortBy, sort_direction: sortDirection }
        : {}),
    }),
    [
      currentPage,
      selectedIds,
      rfiIds,
      providerTypes,
      indications,
      companySizeFiltersId,
      selectedRevenueRange,
      revenueRangeString,
      includeRangeRevenue,
      reportAvailable,
      dataAvailability,
      includeReportAvailable,
      countries,
      states,
      cities,
      searchQuery,
      sortBy,
      sortDirection,
    ]
  );

  // Fetch providers function
  const fetchProviders = async () => {
    const response = await get_providers.getAllProvider(payload, {
      headers: { "Content-Type": "application/json" },
    });

    // Always copy + dedupe so React Query cache / Redux never share a mutated array.
    // Default logo-first order comes from the API (ApplyDiscoverySorting); do not re-sort client-side.
    const items = dedupeProvidersById(response.items || []);

    dispatch(setProviders(items));
    return { ...response, items };
  };

  // UseQuery hook to fetch providers based on the payload
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [
      "search-providers",
      payload,
      sortBy,
      sortDirection,
      sortFetchNonce,
    ],
    queryFn: fetchProviders,
    enabled: !!currentPage, // Run search with or without filters; backend is null-safe and search_text is sent as "" when empty
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (v5; cacheTime alias still works)
    retry: 1,
    structuralSharing: false,
  });

  return {
    providers: data?.items || [],
    totalPages: data?.total_pages || 0,
    isLoading,
    isError: error?.message,
    totalItems: data?.total_items,
    refetch,
  };
};
